import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  FileText, Truck, Save, Loader2, ShieldCheck, Info, ExternalLink, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import RichEditor from "../../components/admin/RichEditor";

const TABS = [
  { key: "cgv", label: "CGV", icon: FileText, description: "Conditions générales de vente appliquées lors de toute commande." },
  { key: "delivery", label: "Livraison & Retours", icon: Truck, description: "Modalités d'expédition, délais, retours et rétractation 14 jours." },
];

const DEFAULT_CGV = `<h2>1. Objet</h2>
<p>Les présentes Conditions Générales de Vente (CGV) régissent les ventes conclues entre <strong>[Raison sociale]</strong> et toute personne physique ou morale passant commande sur ce site.</p>
<h2>2. Prix</h2>
<p>Les prix sont indiqués en euros, toutes taxes comprises (TTC). Les frais de livraison éventuels sont précisés avant validation de la commande.</p>
<h2>3. Commande</h2>
<p>La commande n'est validée qu'après acceptation des présentes CGV et confirmation du paiement. Un e-mail récapitulatif est envoyé à l'adresse renseignée.</p>
<h2>4. Paiement</h2>
<p>Le paiement s'effectue en ligne par carte bancaire via notre prestataire sécurisé <strong>Stripe</strong>. Les données de carte ne transitent pas par nos serveurs.</p>
<h2>5. Livraison</h2>
<p>Les modalités de livraison, délais et frais sont détaillés sur la page <a href="/livraison-retours">Livraison &amp; Retours</a>.</p>
<h2>6. Droit de rétractation</h2>
<p>Conformément à l'article L.221-18 du Code de la consommation, le client dispose d'un délai de <strong>14 jours</strong> à compter de la réception du produit pour exercer son droit de rétractation.</p>
<h2>7. Garanties</h2>
<p>Les produits bénéficient de la garantie légale de conformité et de la garantie des vices cachés (Code civil, art. 1641 et suivants).</p>
<h2>8. Données personnelles</h2>
<p>Les données collectées sont traitées conformément à notre <a href="/confidentialite">Politique de confidentialité</a>.</p>
<h2>9. Droit applicable — Litiges</h2>
<p>Les présentes CGV sont régies par le droit français. En cas de litige, une solution amiable sera recherchée. À défaut, les tribunaux français seront seuls compétents.</p>`;

const DEFAULT_DELIVERY = `<h2>Modes et délais</h2>
<p>Les commandes sont préparées sous 24-48h ouvrées et expédiées depuis la France métropolitaine.</p>
<h2>Frais de livraison</h2>
<p>Calculés automatiquement selon le poids et la destination. Offerts dès [montant] € d'achat.</p>
<h2>Suivi</h2>
<p>Un e-mail avec numéro de suivi est envoyé dès l'expédition.</p>`;

const DEFAULT_RETURNS = `<h2>Droit de rétractation — 14 jours</h2>
<p>Vous disposez d'un délai de <strong>14 jours</strong> à compter de la réception pour exercer votre droit de rétractation, sans justification ni pénalités.</p>
<h2>Retours</h2>
<p>Les produits doivent être retournés dans leur emballage d'origine. Le remboursement intervient sous 14 jours maximum après réception du retour.</p>
<h2>Produits défectueux</h2>
<p>Signaler tout défaut sous 7 jours à [email] avec photos. Remplacement ou remboursement rapide.</p>`;

export default function LegalDocsAdmin() {
  const { settings, refresh } = useSettings();
  const [tab, setTab] = useState("cgv");
  const [form, setForm] = useState({
    cgv_content: "",
    cgv_checkbox_label: "",
    cgv_required_checkout: true,
    cgv_required_booking: true,
    delivery_policy_content: "",
    returns_policy_content: "",
    delivery_returns_enabled: true,
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setForm({
      cgv_content: settings.cgv_content || "",
      cgv_checkbox_label: settings.cgv_checkbox_label || "J'ai lu et j'accepte les conditions générales de vente",
      cgv_required_checkout: settings.cgv_required_checkout !== false,
      cgv_required_booking: settings.cgv_required_booking !== false,
      delivery_policy_content: settings.delivery_policy_content || "",
      returns_policy_content: settings.returns_policy_content || "",
      delivery_returns_enabled: settings.delivery_returns_enabled !== false,
    });
    setDirty(false);
  }, [settings]);

  const patch = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (tab === "cgv") payload.cgv_updated_at = new Date().toISOString();
      if (tab === "delivery") payload.delivery_returns_updated_at = new Date().toISOString();
      await api.put("/settings", payload);
      await refresh();
      setDirty(false);
      toast.success("Documents juridiques enregistrés");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const loadTemplate = (which) => {
    if (which === "cgv") patch("cgv_content", DEFAULT_CGV);
    if (which === "delivery") patch("delivery_policy_content", DEFAULT_DELIVERY);
    if (which === "returns") patch("returns_policy_content", DEFAULT_RETURNS);
    toast.success("Modèle chargé — pensez à l'adapter");
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="legal-docs-admin">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-1">
            <ShieldCheck className="w-3.5 h-3.5" /> Compliance boutique
          </div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Documents juridiques</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
            Rédigez vos <strong>CGV</strong> et votre politique <strong>Livraison &amp; Retours</strong>. Ces documents sont cités dans les cases à cocher du checkout et de la réservation.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/cgv" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold h-9 px-3 rounded-md border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]" data-testid="preview-cgv">
            Voir CGV <ExternalLink className="w-3 h-3" />
          </a>
          <a href="/livraison-retours" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold h-9 px-3 rounded-md border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]" data-testid="preview-delivery">
            Voir Livraison <ExternalLink className="w-3 h-3" />
          </a>
          <Button onClick={save} disabled={!dirty || saving} className="bg-[color:var(--brand-navy)]" data-testid="legal-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            {dirty ? "Enregistrer" : "Enregistré"}
          </Button>
        </div>
      </header>

      <Card className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
        <div className="flex items-start gap-3 text-sm">
          <Info className="w-5 h-5 shrink-0 mt-0.5 text-amber-700" />
          <div className="text-amber-900">
            <strong>Obligatoire en France</strong> : toute boutique en ligne doit afficher des CGV et une politique de retour/rétractation claires.
            Les modèles ci-dessous sont un <strong>point de départ fourni par votre agence</strong> : adaptez-les à votre activité, ou mieux, faites-les valider par un avocat.
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[hsl(var(--border))]">
        {TABS.map(t => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition ${
                active
                  ? "border-[color:var(--brand-navy)] text-[color:var(--brand-navy)]"
                  : "border-transparent text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]"
              }`}
              data-testid={`legal-tab-${t.key}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "cgv" && (
        <div className="space-y-5">
          <Card className="p-5 space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Contenu des CGV</h3>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Affichées sur <code className="font-mono text-[11px] bg-[hsl(var(--muted))]/60 px-1 py-0.5 rounded">/cgv</code>. Utilisez H2 pour les sections, liens internes pour /confidentialite, /livraison-retours.</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => loadTemplate("cgv")} className="text-xs shrink-0" data-testid="load-cgv-template">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Charger le modèle MSC
              </Button>
            </div>
            <RichEditor value={form.cgv_content} onChange={(v) => patch("cgv_content", v)} minHeight={500} placeholder="Rédigez vos conditions générales de vente…" />
          </Card>

          <Card className="p-5 space-y-4">
            <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Case à cocher au checkout &amp; à la réservation</h3>
            <div>
              <Label className="text-xs font-bold">Libellé affiché à côté de la case</Label>
              <Input
                value={form.cgv_checkbox_label}
                onChange={(e) => patch("cgv_checkbox_label", e.target.value)}
                placeholder="J'ai lu et j'accepte les conditions générales de vente"
                className="mt-1.5"
                data-testid="cgv-checkbox-label"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <label className="flex items-start gap-3 p-3 rounded-xl border bg-[hsl(var(--muted))]/30 cursor-pointer" data-testid="cgv-required-checkout-wrapper">
                <Switch checked={form.cgv_required_checkout} onCheckedChange={(v) => patch("cgv_required_checkout", v)} data-testid="cgv-required-checkout" />
                <div>
                  <div className="text-sm font-bold text-[color:var(--brand-navy)]">Obligatoire au paiement boutique</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))]">Le bouton "Passer au paiement" est bloqué tant que la case n'est pas cochée.</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-xl border bg-[hsl(var(--muted))]/30 cursor-pointer" data-testid="cgv-required-booking-wrapper">
                <Switch checked={form.cgv_required_booking} onCheckedChange={(v) => patch("cgv_required_booking", v)} data-testid="cgv-required-booking" />
                <div>
                  <div className="text-sm font-bold text-[color:var(--brand-navy)]">Obligatoire à la réservation</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))]">Le bouton "Valider la réservation" est bloqué tant que la case n'est pas cochée.</div>
                </div>
              </label>
            </div>
            <div className="text-[11px] text-[hsl(var(--muted-foreground))] flex items-center gap-1.5 italic">
              <Info className="w-3 h-3" />
              Pour conformité RGPD/DGCCRF, gardez ces 2 options activées par défaut.
            </div>
          </Card>
        </div>
      )}

      {tab === "delivery" && (
        <div className="space-y-5">
          <Card className="p-4 flex items-center gap-3 flex-wrap">
            <Switch checked={form.delivery_returns_enabled} onCheckedChange={(v) => patch("delivery_returns_enabled", v)} data-testid="delivery-enabled" />
            <div className="flex-1 min-w-[200px]">
              <div className="text-sm font-bold text-[color:var(--brand-navy)]">Afficher la page /livraison-retours</div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))]">Lien ajouté dans le footer et dans les cases CGV.</div>
            </div>
            {form.delivery_returns_enabled ? (
              <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] font-bold">Publié</Badge>
            ) : (
              <Badge className="bg-slate-200 text-slate-700 border-0 text-[10px] font-bold">Masqué</Badge>
            )}
          </Card>

          <Card className="p-5 space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Politique de livraison</h3>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Modes (transporteur, coursier, Click &amp; Collect), délais, frais, suivi de commande.</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => loadTemplate("delivery")} className="text-xs shrink-0" data-testid="load-delivery-template">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Charger le modèle
              </Button>
            </div>
            <RichEditor value={form.delivery_policy_content} onChange={(v) => patch("delivery_policy_content", v)} minHeight={320} placeholder="Décrivez vos modes de livraison, délais, frais…" />
          </Card>

          <Card className="p-5 space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Politique de retours &amp; rétractation</h3>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Droit de rétractation 14 jours, procédure de retour, remboursements, produits défectueux.</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => loadTemplate("returns")} className="text-xs shrink-0" data-testid="load-returns-template">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Charger le modèle
              </Button>
            </div>
            <RichEditor value={form.returns_policy_content} onChange={(v) => patch("returns_policy_content", v)} minHeight={320} placeholder="Procédure de retour, délais de remboursement…" />
          </Card>
        </div>
      )}

      {dirty && (
        <div className="sticky bottom-4 flex justify-center z-30">
          <Card className="p-3 bg-[color:var(--brand-navy)] text-white border-0 shadow-2xl flex items-center gap-3">
            <span className="text-xs">Modifications non enregistrées</span>
            <Button size="sm" onClick={save} disabled={saving} className="bg-[color:var(--brand-sky)] hover:bg-[color:var(--brand-sky)]/90" data-testid="legal-save-floating">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Enregistrer
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
}
